import React from "react";
import { color, space, radius, text, weight, numeric } from "../tokens";

/**
 * <RolePanel> — the workhorse container for a differentiated dashboard panel.
 *
 * The panel's IDENTITY is signalled by (a) a 2px top accent bar in the
 * role color, (b) a role-colored icon in the header, and (c) an optional
 * <LiveDot> in the top-right. Body stays on the neutral surface — this
 * keeps every panel consistent while still giving each one a voice.
 *
 * Datadog / Vanta / PostHog pattern.
 *
 *   role      : "live" | "ai" | "alert" | "compliance" | "neutral"
 *   title     : string
 *   subtitle  : string | ReactNode (optional, short)
 *   icon      : ReactNode (Lucide icon at 16px, will inherit the role color)
 *   count     : number (optional, shown as inline muted count next to title)
 *   right     : ReactNode (top-right slot — usually <LiveDot> or a menu)
 *
 * Do NOT wrap another Card / Card-like element inside a RolePanel — the
 * container already provides the surface. Content sits directly inside.
 */
export function RolePanel({
  role = "neutral",
  title,
  subtitle,
  icon,
  count,
  right,
  children,
  style,
}) {
  const palette = color.role[role] || color.role.neutral;
  // Subtle gradient wash below the accent bar — role color fades from
  // ~7% opacity at the top into pure surface within ~72px. Adds identity
  // without shouting. Datadog / Vercel / Grafana panel-header pattern.
  const washColor = palette.solid;
  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${washColor}12 0%, ${washColor}00 72px), ${color.bg.surface}`,
        border: `1px solid ${color.border.subtle}`,
        borderTop: `2px solid ${palette.solid}`,
        borderRadius: radius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: `0 1px 2px rgba(10, 10, 10, 0.03)`,
        ...style,
      }}
    >
      <div
        style={{
          padding: "16px 20px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: space[3],
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {icon && (
            <span style={{ display: "inline-flex", color: palette.solid, flexShrink: 0 }}>
              {icon}
            </span>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: space[2] }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </h3>
              {(count !== undefined && count !== null) && (
                <span style={{ fontSize: text.base, color: color.text.muted, ...numeric.tabular }}>
                  · {count}
                </span>
              )}
            </div>
            {subtitle && (
              <div style={{ fontSize: 12, color: color.text.muted }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {right && <div style={{ flexShrink: 0 }}>{right}</div>}
      </div>
      <div style={{ padding: "0 20px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}
